import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { setInitialPassword } from './api/passwordReset';
import { MIN_PASSWORD_LENGTH, inputClass, labelClass, SubmitButton, ErrorBox } from './ForgotPassword';

/* Landing page of the link emailed to newly created staff accounts (/set-password?token=...).
   The account can't sign in until this is done: it is created with an unusable random password. */
const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await setInitialPassword(token, password);
      setIsDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const title = isDone ? 'Password set' : !token ? 'Invalid link' : 'Set your password';
  const subtitle = isDone
    ? 'Your account is ready. You can now sign in.'
    : !token
      ? 'This link is missing its token. Open the link from your email again, or ask an administrator to resend it.'
      : `Choose a password with at least ${MIN_PASSWORD_LENGTH} characters to activate your account.`;

  return (
    <div className="min-h-dvh flex items-center justify-center bg-gradient-to-b from-gray-50/60 via-white to-white px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src="/pics/atk-long.svg" alt="ATK" className="w-52 sm:w-60" />
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">{title}</h1>
            <p className="mt-1 text-sm text-gray-500 break-words">{subtitle}</p>
          </div>

          {isDone ? (
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:brightness-110 active:scale-[0.99]"
            >
              Go to sign in
            </button>
          ) : token ? (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label className={labelClass} htmlFor="new-password">Password</label>
                <input
                  type="password"
                  id="new-password"
                  autoFocus
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="Enter a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="confirm-password">Confirm password</label>
                <input
                  type="password"
                  id="confirm-password"
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="Repeat the password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              {error && <ErrorBox text={error} />}
              <SubmitButton isLoading={isLoading} loadingLabel="Saving...">Set password</SubmitButton>
            </form>
          ) : null}

          {!isDone && (
            <div className="mt-6 text-center text-sm">
              <Link to="/login" className="text-gray-500 hover:text-gray-900">
                ← Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
